import { StateContext } from '@ngxs/store';
import { Column } from '@orion-platform/datagrid';
import { EMPTY, Observable } from 'rxjs';
import { compareByFieldOrder } from '../../utils/datagrid.utils';
import { sameValues } from '../../utils/general.utils';
import { EditUserSettings } from '../supply-visibility/supply-visibility.actions';
import { SlotStorage } from './slots.types';

export const applyFilterUpdate = <T extends Record<string, unknown>, K>(
  ctx: StateContext<K>,
  original: T,
  next: T,
  patch: Partial<K>,
  storageKey: string,
): Observable<void> => {
  if (sameValues(original, next)) return EMPTY;
  ctx.patchState(patch);
  return ctx.dispatch(new EditUserSettings({ data: JSON.stringify(next), key: storageKey }, false));
};

/**
 * Computes the list of selected docks that are actually allowed by the currently
 * selected storages. In other words: intersect `filter.docks` with the docks
 * available in `storageDocksList` for the storages in `filter.storages`.
 * @param {T} filter - The current filter containing user-selected `docks` and `storages`.
 * @param {Storage[]} storageDocksList - A list mapping each storage to its available docks.
 * @returns {string[]} -The subset of `filter.docks` that belong to any storage in `filter.storages`.
 */
export const getAlllowedDocks = <T extends { docks: string[]; storages: string[] }>(
  filter: T,
  storageDocksList: SlotStorage[],
): string[] => {
  const allowedDocks = processVisibleDocks(filter, storageDocksList).map((d): string => d.dock);

  // Keep only docks that belong to the selected storages
  return filter.docks ? filter.docks.filter((dock): boolean => allowedDocks.includes(dock)) : [];
};

/**
 * Expands `storageDocksList` to the list of **visible** docks for the storages
 * currently selected in `filter`. Unlike `getAlllowedDocks`, it does not intersect
 * with `filter.docks`; it returns all docks under the selected storages.
 * @param {T} filter - The current filter containing user-selected `docks` and `storages`.
 * @param {Storage[]} storageDocksList - A list mapping each storage to its available docks.
 * @returns {{ dock: string }[]} - An array of objects in the shape `{ dock: string }`, containing every dock
 * belonging to the storages listed in `filter.storages`.
 */
export const processVisibleDocks = <T extends { docks: string[]; storages: string[] }>(
  filter: T,
  storageDocksList: SlotStorage[],
): Array<{ dock: string }> => {
  if (!storageDocksList || !(filter && filter.storages)) {
    return [];
  }

  // Flatten all docks for selected storages
  return storageDocksList
    .filter((group): boolean => filter.storages.includes(group['storage']))
    .reduce<Array<{ dock: string }>>((acc, group): Array<{ dock: string }> => {
      for (const dock of group['docks']) acc.push({ dock });
      return acc;
    }, []);
};

/**
 * Build the set of column "fields" that are allowed to appear in the table.
 *
 * - `formFields` comes from form settings (array of strings).
 * - We lower-case everything so comparisons are case-insensitive.
 * - We always allow the special "actions" column (buttons/menus/etc.).
 */
export const buildAllowedSet = (formFields: string[]): Set<string> => {
  return new Set(['actions', ...formFields.map((f): string => f.toLowerCase())]);
};

/**
 * Build the "fallback" column list when we don't have user-stored settings.
 *
 * What it does:
 * - Adds an `index` to each column (UI use this for rendering/order)
 * - Sorts columns using `compareByFieldOrder(formFields)` so the order matches
 *   the configured order in `formFields`.
 */
export const buildFallbackColumns = (defaults: Column[], formFields: string[]): Column[] => {
  return defaults
    .map((col, index): Column & { index: number } => ({ ...col, index }))
    .sort(compareByFieldOrder(formFields));
};
